<script setup lang="ts">
import VideoCallParticipantVolumeControl from "~/components/video-call/VideoCallParticipantVolumeControl.vue";
import type {
  VideoCallParticipantState,
  VideoCallQualityProfile,
} from "~/types/videoCalls";
import {
  getPeerConnectionLabel,
  getVideoCallTilePresentation,
} from "~/utils/videoCalls/policy";

const props = withDefaults(
  defineProps<{
    stream: MediaStream | null;
    name: string;
    local?: boolean;
    cameraEnabled?: boolean;
    microphoneEnabled?: boolean;
    connectionState?: RTCPeerConnectionState | null;
    participantState?: VideoCallParticipantState | null;
    accountLinked?: boolean;
    removable?: boolean;
    muted?: boolean;
    volumePercent?: number;
    volumeSupported?: boolean;
    qualityProfile?: VideoCallQualityProfile | null;
    /** Fill the available layout cell instead of enforcing the prejoin ratio. */
    fill?: boolean;
  }>(),
  {
    local: false,
    cameraEnabled: true,
    microphoneEnabled: true,
    connectionState: null,
    participantState: null,
    accountLinked: false,
    removable: false,
    muted: false,
    volumePercent: 100,
    volumeSupported: true,
    qualityProfile: null,
    fill: false,
  },
);

const emit = defineEmits<{
  remove: [];
  "update:volumePercent": [volumePercent: number];
}>();

const videoElement = ref<HTMLVideoElement | null>(null);
const initials = computed(() =>
  props.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join(""),
);
const connectionLabel = computed(() =>
  getPeerConnectionLabel({
    connectionState: props.connectionState,
    participantState: props.participantState,
  }),
);
const tilePresentation = computed(() =>
  getVideoCallTilePresentation({
    local: props.local,
    cameraEnabled: props.cameraEnabled,
    qualityProfile: props.qualityProfile,
  }),
);
const adaptiveVideoPaused = computed(
  () => tilePresentation.value === "adaptive-paused",
);
const reducedQuality = computed(
  () => tilePresentation.value === "adaptive-reduced",
);

const attachStream = () => {
  if (videoElement.value && videoElement.value.srcObject !== props.stream) {
    videoElement.value.srcObject = props.stream;
  }
};

watch(() => props.stream, attachStream);
onMounted(attachStream);
onBeforeUnmount(() => {
  if (videoElement.value) videoElement.value.srcObject = null;
});
</script>

<template>
  <article class="video-tile" :class="{ 'video-tile--fill': fill }">
    <video
      v-show="stream && cameraEnabled && !adaptiveVideoPaused"
      ref="videoElement"
      :class="{ 'video-tile__video--mirrored': local }"
      autoplay
      playsinline
      :muted="local || muted"
      :aria-label="local ? `Sua câmera: ${name}` : `Câmera de ${name}`"
    />
    <div
      v-if="!stream || !cameraEnabled || adaptiveVideoPaused"
      class="video-tile__placeholder"
    >
      <span>{{ initials || "?" }}</span>
      <small v-if="!cameraEnabled">Câmera desligada</small>
      <small v-else-if="adaptiveVideoPaused">
        Vídeo pausado para preservar o áudio
      </small>
      <small v-else>Conectando vídeo...</small>
    </div>

    <footer>
      <div class="video-tile__identity">
        <span class="video-tile__name"
          >{{ name }}{{ local ? " (você)" : "" }}</span
        >
        <span
          v-if="accountLinked"
          class="video-tile__account-badge"
          data-testid="video-call-participant-account-badge"
        >
          Conta TeachPlus
        </span>
        <i
          class="mdi"
          :class="microphoneEnabled ? 'mdi-microphone' : 'mdi-microphone-off'"
          :aria-label="
            microphoneEnabled ? 'Microfone ligado' : 'Microfone desligado'
          "
        />
      </div>
      <div
        v-if="$slots.actions || !local || removable"
        class="video-tile__actions"
      >
        <slot name="actions" />
        <VideoCallParticipantVolumeControl
          v-if="!local"
          :participant-name="name"
          :model-value="volumePercent"
          :supported="volumeSupported"
          @update:model-value="emit('update:volumePercent', $event)"
        />
        <button
          v-if="removable"
          type="button"
          class="video-tile__remove-button"
          :aria-label="`Remover ${name} da chamada`"
          :title="`Remover ${name} da chamada`"
          @click="emit('remove')"
        >
          <i class="mdi mdi-account-remove-outline" />
        </button>
      </div>
    </footer>

    <span v-if="connectionLabel" class="video-tile__connection">
      {{ connectionLabel }}
    </span>
    <span v-if="reducedQuality" class="video-tile__quality" role="status">
      Vídeo reduzido para preservar o áudio
    </span>
  </article>
</template>

<style scoped lang="scss">
.video-tile {
  position: relative;
  width: 100%;
  /* A portrait phone track must not become the height of the call panel. */
  min-height: min(160px, 54dvh);
  max-height: min(420px, 54dvh);
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 14px;
  background: #172033;
  color: #fff;
  isolation: isolate;
}

.video-tile--fill {
  height: 100%;
  min-height: 0;
  max-height: none;
  aspect-ratio: auto;
}

video,
.video-tile__placeholder {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  object-fit: cover;
}

video {
  display: block;
  background: #111827;
}

.video-tile__video--mirrored {
  transform: scaleX(-1);
}

.video-tile__placeholder {
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 8px;
  background:
    radial-gradient(circle at 30% 20%, rgba(49, 86, 255, 0.4), transparent 38%),
    #1f2a44;

  span {
    display: grid;
    place-items: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    font-size: 1.3rem;
    font-weight: 700;
  }

  small {
    color: #dce4f5;
  }
}

footer {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 18px 10px 8px;
  background: linear-gradient(transparent, rgba(6, 10, 20, 0.86));
}

.video-tile__identity {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 600;

  .video-tile__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.video-tile__account-badge {
  flex: 0 0 auto;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(219, 247, 234, 0.94);
  color: #155f42;
  font-size: 0.58rem;
  font-weight: 700;
  white-space: nowrap;
}

.video-tile__actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 6px;
}

.video-tile__remove-button {
  display: grid;
  place-items: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: rgba(176, 0, 32, 0.88);
  color: white;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid rgba(255, 184, 195, 0.9);
    outline-offset: 2px;
  }
}

.video-tile__connection {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.82);
  font-size: 0.7rem;
}

.video-tile__quality {
  position: absolute;
  top: 8px;
  right: 8px;
  max-width: calc(100% - 112px);
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.82);
  font-size: 0.66rem;
  line-height: 1.25;
  text-align: right;
}
</style>
